// src/pages/entit/met.js
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import metData from "../../assets/data/met.json";
import "../../App.css";

// ⭐ added
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";
import { METASTASIS_COLORS } from "../../config/colors";

// ⭐ excluded entities
const EXCLUDED_ENTITS = [
  "Chronische lymphatische Leukämie (CLL/SLL)",
  "Multiples Myelom",
  "Diffuses großzelliges B-Zell-Lymphom (DLBCL)",
];

// --- formatters ---
const nfInt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nfPct1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));
const fmtPct = (v) => (v == null ? "–" : nfPct1.format(v));

const useAllMetsAndColors = () => {
  const allMets = useMemo(
    () => [...new Set(metData.map((d) => d.Met))].sort((a, b) => a.localeCompare(b, "de")),
    []
  );


  const colorMap = useMemo(() => {
    const m = {};
    allMets.forEach((label, i) =>(m[label] = METASTASIS_COLORS[i % METASTASIS_COLORS.length]));
    return m;
  }, [allMets]);

  return { allMets, colorMap };
};

const Met = () => {
  const { allMets, colorMap } = useAllMetsAndColors();

  const years = useMemo(
    () => [...new Set(metData.map((d) => d.diayear))].sort((a, b) => (a > b ? 1 : -1)),
    []
  );

  // 🔹 entities from data + excluded ones appended (so original default stays valid)
 const entits = useMemo(() => {
  const set = new Set([
    ...metData.map((d) => d.entit),
    ...EXCLUDED_ENTITS,          // add excluded ones into the same set
  ]);

  return [...set].sort((a, b) => a.localeCompare(b, "de"));  // then sort everything together
}, []);

const [year, setYear] = useState(
  String(years[years.length - 1] ?? "")
);
  const [entit, setEntit] = useState(entits[0] ?? "");

  const isExcluded = EXCLUDED_ENTITS.includes(entit);

  const filtered = useMemo(
    () => metData.filter((d) => String(d.diayear) === String(year) && d.entit === entit),
    [year, entit]
  );

  const maleRows = useMemo(() => filtered.filter((d) => d.SEX === "M"), [filtered]);
  const femaleRows = useMemo(() => filtered.filter((d) => d.SEX === "W"), [filtered]);

  const totalM = useMemo(() => maleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [maleRows]);
  const totalW = useMemo(() => femaleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [femaleRows]);
  const totalAll = totalM + totalW;

  const pieDataFor = (rows) => {
    const byMet = rows.filter((r) => r.pct != null);
    byMet.sort((a, b) => allMets.indexOf(a.Met) - allMets.indexOf(b.Met));
    return byMet.map((r) => ({
      name: r.Met,
      value: Number(r.pct),
      n: Number(r.n),
      color: colorMap[r.Met] || METASTASIS_COLORS[0],
    }));
  };

  const malePie = useMemo(() => pieDataFor(maleRows), [maleRows, allMets, colorMap]);
  const femalePie = useMemo(() => pieDataFor(femaleRows), [femaleRows, allMets, colorMap]);

  const basePieOption = {
    tooltip: {
      trigger: "item",
      backgroundColor: "#004c3f",
      borderWidth: 0,
      textStyle: { color: "#fff", fontSize: 12 },
      confine: true,
      formatter: (p) =>
        `<div style="text-align:left;line-height:1.3">
          <div style="font-weight:600;margin-bottom:4px">${p.name}</div>
          <div>Anzahl: ${fmtInt(p.data?.n)}</div>
          <div>Anteil: ${fmtPct(p.value)}%</div>
        </div>`,
    },
    legend: { show: false },
    series: [],
  };

  // ⭐ add white borders
  const menOption = {
    ...basePieOption,
    series: [
      {
        name: "Männer",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: {
          show: true,
          formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`,
          fontSize: 12,
        },
        labelLine: { show: true, length: 12, length2: 8 },
        data: malePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  // ⭐ add white borders
  const womenOption = {
    ...basePieOption,
    series: [
      {
        name: "Frauen",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: {
          show: true,
          formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`,
          fontSize: 12,
        },
        labelLine: { show: true, length: 12, length2: 8 },
        data: femalePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const tableRows = useMemo(() => {
    const map = new Map();
    const add = (row, sexKey) => {
      const key = row.Met;
      if (!map.has(key)) {
        map.set(key, {
          label: key,
          n_m: null,
          n_w: null,
          pct_m: null,
          pct_w: null,
        });
      }
      const rec = map.get(key);
      if (sexKey === "M") {
        rec.n_m = Number(row.n);
        rec.pct_m = Number(row.pct);
      } else {
        rec.n_w = Number(row.n);
        rec.pct_w = Number(row.pct);
      }
    };
    maleRows.forEach((r) => add(r, "M"));
    femaleRows.forEach((r) => add(r, "W"));

    const arr = [...map.values()].map((r) => {
      const n_g = (r.n_m || 0) + (r.n_w || 0);
      const pct_g = totalAll > 0 ? (n_g / totalAll) * 100 : null;
      return { ...r, n_g, pct_g };
    });

    arr.sort((a, b) => {
      if (a.label === "andere") return 1;
      if (b.label === "andere") return -1;
      return allMets.indexOf(a.label) - allMets.indexOf(b.label);
    });
    return arr;
  }, [maleRows, femaleRows, totalAll, allMets]);

  const footer = useMemo(() => {
    const n_g = totalAll;
    const n_m = totalM;
    const n_w = totalW;
    return {
      n_g,
      n_m,
      n_w,
      pct_g: 100,
      pct_m: totalM > 0 ? 100 : null,
      pct_w: totalW > 0 ? 100 : null,
    };
  }, [totalAll, totalM, totalW]);

  const LegendMet = ({ cats }) => {
  if (!cats?.length) return null;
  return (
    <div style={{ fontSize: 12, margin: "6px 0 10px" }}>
      {cats.map((c) => (
        <span key={c} style={{ marginRight: 16 }}>
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: 3,            // same as Stage
              backgroundColor: colorMap[c],
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {c}
        </span>
      ))}
    </div>
  );
};


  const maleLegendCats = useMemo(() => malePie.map((d) => d.name), [malePie]);
  const femaleLegendCats = useMemo(() => femalePie.map((d) => d.name), [femalePie]);

  // ⭐ EXPORT HANDLER
  const handleExport = () => {
    if (isExcluded) {
      // button is disabled anyway, but guard just in case
      return;
    }

    const excelRows = tableRows.map((r) => ({
      Metastase: r.label,
      Gesamt: fmtInt(r.n_g),
      Männer: fmtInt(r.n_m),
      Frauen: fmtInt(r.n_w),
      "Gesamt (%)": fmtPct(r.pct_g),
      "Männer (%)": fmtPct(r.pct_m),
      "Frauen (%)": fmtPct(r.pct_w),
    }));

    exportToExcel({
      filename: "metastasierung",
      sheetName: "Metastasierung",
      data: excelRows,
      metadata: [
        ["Metastasierung"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Metastasierung</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Metastasen sind Tochtergeschwülste des Primärtumors in andere
          Organsysteme. Die Metastasierung einer Krebserkrankung hat einen
          entscheidenden Einfluss auf die Therapiemöglichkeiten und die
          Prognose der Erkrankung.
        </p>
        <p>
          Sie können hier für die 15 häufigsten Entitäten für verschiedene
          Diagnosejahre die häufigsten Organe der Metastasierung für Männer und
          Frauen getrennt anzeigen lassen. Dargestellt ist die Metastasierung
          zum Diagnosezeitpunkt.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}
      <button
        onClick={handleExport}
        className="top-btn mb-3"
        disabled={isExcluded}
      >
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}
      <div className="d-flex align-items-center gap-3 mb-4" style={{ maxWidth: 720 }}>
        <Form.Group controlId="entitSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select value={entit} onChange={(e) => setEntit(e.target.value)}>
            {entits.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearSelect" className="flex-fill">
          <Form.Label>Jahr:</Form.Label>
          <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {isExcluded ? (
        <p className="mt-3" style={{ fontSize: "0.95rem", color: "#555" }}>
          Für diese Entität liegen keine Angaben zur Metastasierung vor.
        </p>
      ) : (
        <>
          {/* Pies */}
          <Row className="mb-4">
            <Col md={6}>
              <h5 className="mb-2">Männer</h5>
              <LegendMet cats={maleLegendCats} />
              <ReactECharts option={menOption} style={{ width: "100%", height: 420 }} />
            </Col>
            <Col md={6}>
              <h5 className="mb-2">Frauen</h5>
              <LegendMet cats={femaleLegendCats} />
              <ReactECharts option={womenOption} style={{ width: "100%", height: 420 }} />
            </Col>
          </Row>

          {/* Table */}
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1000px" }}
          >
            <thead>
              <tr>
                <th className="text-start">Metastase</th>
                <th colSpan={3}>Anzahl</th>
                <th colSpan={3}>Anteil in %</th>
              </tr>
              <tr>
                <th className="text-start"></th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr key={`met-${r.label}`}>
                  <td className="text-start">{r.label}</td>
                  <td>{fmtInt(r.n_g)}</td>
                  <td>{fmtInt(r.n_m)}</td>
                  <td>{fmtInt(r.n_w)}</td>
                  <td>{fmtPct(r.pct_g)}</td>
                  <td>{fmtPct(r.pct_m)}</td>
                  <td>{fmtPct(r.pct_w)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-secondary">
                <td className="text-start fw-bold">Gesamt</td>
                <td className="fw-bold">{fmtInt(footer.n_g)}</td>
                <td className="fw-bold">{fmtInt(footer.n_m)}</td>
                <td className="fw-bold">{fmtInt(footer.n_w)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_g)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_m)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_w)}</td>
              </tr>
            </tfoot>
          </Table>
        </>
      )}
    </Container>
  );
};

export default Met;

